import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Home,
  Building,
  Calendar,
  Camera,
  BarChart3,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  User,
  UserPlus,
  Copy,
  Check,
  type LucideIcon,
} from 'lucide-react';
import { useAppStore } from '../store';
import { useAdminPendingCount } from '../store/selectors';
import { useAuth } from '../contexts/AuthContext';
import { useViewMode } from '../contexts/ViewModeContext';
import { cn } from '../lib/utils';
import ViewModeToggle from './ViewModeToggle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { currentUser, createInvitation } = useAppStore();
  const { signOut } = useAuth();
  const { isGuestView, canToggleView } = useViewMode();

  const adminPendingCount = useAdminPendingCount();

  // Invite modal state
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'family' | 'familyMember' | 'guest'>('guest');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<{ token: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const canInvite = (currentUser?.isAdmin || currentUser?.isFamily) && !isGuestView;

  const handleOpenInviteModal = () => {
    setInviteEmail('');
    setInviteRole('guest');
    setInviteError(null);
    setInviteSuccess(null);
    setCopied(false);
    setInviteModalOpen(true);
  };

  const handleSubmitInvite = async () => {
    if (!inviteEmail.trim()) return;

    setInviteSubmitting(true);
    setInviteError(null);

    const result = await createInvitation(inviteEmail.trim(), inviteRole);

    if (result.success && result.token) {
      setInviteSuccess({ token: result.token });
    } else {
      setInviteError(result.error || 'Einladung konnte nicht gesendet werden');
    }

    setInviteSubmitting(false);
  };

  const handleCopyLink = async () => {
    if (!inviteSuccess) return;
    const inviteLink = `${window.location.origin}/invite/${inviteSuccess.token}`;
    await navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  type NavItem = {
    name: string;
    href: string;
    icon: LucideIcon;
    hideInGuestView: boolean;
    hideForFamilyMember: boolean;
    badge?: number;
  };

  const navigation: NavItem[] = [
    { name: t('nav.home'), href: '/', icon: Home, hideInGuestView: true, hideForFamilyMember: true },
    { name: t('nav.properties'), href: '/homes', icon: Building, hideInGuestView: false, hideForFamilyMember: false },
    { name: t('nav.myBookings'), href: '/bookings', icon: Calendar, hideInGuestView: false, hideForFamilyMember: false },
    { name: t('nav.brags'), href: '/brag-board', icon: Camera, hideInGuestView: false, hideForFamilyMember: false },
    { name: t('nav.analytics'), href: '/analytics', icon: BarChart3, hideInGuestView: true, hideForFamilyMember: true },
    { name: t('nav.profile'), href: '/profile', icon: User, hideInGuestView: false, hideForFamilyMember: false },
    ...(currentUser?.isAdmin ? [{ name: t('nav.admin'), href: '/admin', icon: Settings, hideInGuestView: true, hideForFamilyMember: true, badge: adminPendingCount }] : []),
  ].filter((item) => {
    if (isGuestView) return !item.hideInGuestView;
    if (currentUser?.isFamilyMember) return !item.hideForFamilyMember;
    return true;
  });

  const isActive = (path: string) => location.pathname === path;

  return (
    <aside
      className={cn(
        'hidden lg:flex flex-col h-screen bg-background/40 backdrop-blur-2xl border-r border-border/50 transition-all duration-300 ease-in-out shadow-[4px_0_24px_-12px_rgba(0,0,0,0.1)]',
        collapsed ? 'w-[72px]' : 'w-64'
      )}
    >
      {/* Logo */}
      <div className={cn(
        'flex items-center h-16 px-4 border-b border-border/50',
        collapsed ? 'justify-center' : 'gap-3'
      )}>
        {!collapsed && (
          <span className="text-lg font-extrabold bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent truncate">
            Holiday Homes der Familie Richner
          </span>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {navigation.map((item) => {
          const Icon = item.icon;
          const badge = item.badge ?? 0;
          return (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                'relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 no-underline group border',
                isActive(item.href)
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-indigo-500/25 border-white/10'
                  : 'border-transparent text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
                collapsed && 'justify-center px-2'
              )}
              title={collapsed ? `${item.name}${badge ? ` (${badge})` : ''}` : undefined}
            >
              <div className="relative flex-shrink-0">
                <Icon className={cn('h-5 w-5', isActive(item.href) ? '' : 'group-hover:scale-110 transition-transform')} />
                {collapsed && badge > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold leading-none ring-2 ring-background">
                    {badge > 9 ? '9+' : badge}
                  </span>
                )}
              </div>
              {!collapsed && (
                <>
                  <span className="flex-1">{item.name}</span>
                  {badge > 0 && (
                    <span className={cn(
                      'inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full text-[10px] font-bold leading-none',
                      isActive(item.href) ? 'bg-white/20 text-white' : 'bg-red-500 text-white'
                    )}>
                      {badge > 99 ? '99+' : badge}
                    </span>
                  )}
                </>
              )}
            </Link>
          );
        })}
      </nav>

      {/* User section */}
      <div className="p-3 border-t border-border/50">
        {!collapsed && currentUser && (
          <div className="px-3 py-2 mb-2">
            <p className="text-sm font-medium text-foreground truncate">{currentUser.name}</p>
            <p className="text-xs text-muted-foreground truncate">{currentUser.email}</p>
          </div>
        )}
        {canToggleView && (
          <div className={cn('mb-2', collapsed ? 'flex justify-center py-2' : 'px-3 py-2')}>
            <ViewModeToggle compact={collapsed} />
          </div>
        )}
{canInvite && (
          <button
            onClick={handleOpenInviteModal}
            className={cn(
              'flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-300',
              'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
              collapsed && 'justify-center px-2'
            )}
            title={collapsed ? 'Einladen' : undefined}
          >
            <UserPlus className="h-5 w-5 flex-shrink-0" />
            {!collapsed && <span>Einladen</span>}
          </button>
        )}
        <button
          onClick={signOut}
          className={cn(
            'flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-all duration-300',
            collapsed && 'justify-center px-2'
          )}
          title={collapsed ? 'Abmelden' : undefined}
        >
          <LogOut className="h-5 w-5 flex-shrink-0" />
          {!collapsed && <span>Abmelden</span>}
        </button>
      </div>

      {/* Collapse toggle */}
      <button
        onClick={onToggle}
        className="hidden lg:flex items-center justify-center h-10 border-t border-border/50 text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors"
      >
        {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
      </button>

      {/* Invite Modal */}
      <Dialog open={inviteModalOpen} onOpenChange={setInviteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Jemanden einladen</DialogTitle>
            <DialogDescription>
              {inviteSuccess
                ? 'Teile diesen Link mit der eingeladenen Person'
                : 'Einladung zu Holiday Homes der Familie Richner senden'}
            </DialogDescription>
          </DialogHeader>

          {inviteSuccess ? (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2">
                <Input
                  value={`${window.location.origin}/invite/${inviteSuccess.token}`}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button variant="outline" size="icon" onClick={handleCopyLink}>
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                Dieser Link läuft in 7 Tagen ab. Sende ihn an{' '}
                <span className="font-medium text-foreground">{inviteEmail}</span>
              </p>
              <DialogFooter>
                <Button onClick={() => setInviteModalOpen(false)}>Fertig</Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="inviteEmail">E-Mail-Adresse</Label>
                  <Input
                    id="inviteEmail"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="friend@example.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="inviteRole">Einladen als</Label>
                  <Select
                    value={inviteRole}
                    onValueChange={(v: 'family' | 'familyMember' | 'guest') => setInviteRole(v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currentUser?.isAdmin && (
                        <SelectItem value="family">Eigentümer</SelectItem>
                      )}
                      {currentUser?.isAdmin && (
                        <SelectItem value="familyMember">Familienmitglied</SelectItem>
                      )}
                      <SelectItem value="guest">Gast</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {inviteRole === 'family'
                      ? 'Eigentümer können buchen (kostenlos), Gäste einladen und haben Zugriff auf alle Funktionen'
                      : inviteRole === 'familyMember'
                        ? 'Familienmitglieder können Unterkünfte buchen (20 CHF/Nacht/Person)'
                        : 'Gäste können Unterkünfte durchsuchen und zu regulären Preisen buchen'}
                  </p>
                </div>

                {inviteError && (
                  <div className="text-sm p-3 rounded-md bg-red-50 text-red-700 border border-red-200">
                    {inviteError}
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setInviteModalOpen(false)}>
                  Abbrechen
                </Button>
                <Button
                  onClick={handleSubmitInvite}
                  disabled={!inviteEmail.trim() || inviteSubmitting}
                  className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white shadow-lg shadow-indigo-500/25 border-0"
                >
                  {inviteSubmitting ? 'Wird gesendet...' : 'Einladung erstellen'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </aside>
  );
};

export default Sidebar;
